/**
 * CL-INVITE-001A — Canonical portal invite status for a contact email.
 * Prefer redeemed / sent journey invites over leftover unused duplicates.
 */

import { normalizeInviteEmail } from "./portalInvite";

export const CANONICAL_INVITE_STATUS = Object.freeze({
  NONE: "none",
  PREPARED_UNUSED: "prepared_unused",
  SENT_UNUSED: "sent_unused",
  REDEEMED: "redeemed",
});

function safeStr(value) {
  return String(value ?? "").trim();
}

export function inviteTimestampMs(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function isInviteInvalidated(invite = {}) {
  if (!invite || typeof invite !== "object") return false;
  if (invite.invalidated === true) return true;
  if (invite.adminInvalidated === true) return true;
  if (invite.adminInvalidatedAt != null) return true;
  const status = safeStr(invite.status).toLowerCase();
  if (
    status === "invalidated" ||
    status === "revoked" ||
    status === "cancelled" ||
    status === "canceled" ||
    status === "disabled" ||
    status === "expired"
  ) {
    return true;
  }
  return false;
}

export function isInviteRedeemed(invite = {}) {
  return invite?.used === true;
}

export function isInviteEmailSent(invite = {}) {
  return invite?.emailSentAt != null;
}

function inviteEmailMatches(invite, emailLower) {
  const em = normalizeInviteEmail(emailLower);
  if (!em) return true;
  const target = normalizeInviteEmail(invite?.targetEmailLower || invite?.email);
  // Legacy invites may omit targetEmailLower when the caller already scoped rows by contact email.
  if (!target) return true;
  return target === em;
}

function sortByNewest(a, b, field = "createdAt") {
  return inviteTimestampMs(b?.[field]) - inviteTimestampMs(a?.[field]);
}

/**
 * Filter invites for one contact email (includes used + unused; excludes invalidated).
 */
export function listInvitesForContactEmail(inviteRows = [], emailLower = "") {
  const em = normalizeInviteEmail(emailLower);
  return (Array.isArray(inviteRows) ? inviteRows : [])
    .filter((inv) => inv && inv.id && !isInviteInvalidated(inv))
    .filter((inv) => (em ? inviteEmailMatches(inv, em) : true))
    .sort((a, b) => sortByNewest(a, b, "createdAt"));
}

/**
 * Canonical priority:
 * 1. redeemed invite matching linked Auth UID
 * 2. most recently sent invite (any used/unused with emailSentAt)
 * 3. most recent unused invite
 * 4. older invites as history / stale only
 *
 * @returns {{
 *   primaryInvite: object|null,
 *   status: string,
 *   statusLabel: string,
 *   detail: string,
 *   staleUnusedInvites: object[],
 *   historyInvites: object[],
 *   allForEmail: object[],
 *   canInvalidateStale: boolean,
 * }}
 */
export function resolveCanonicalPortalInviteStatus({
  invites = [],
  emailLower = "",
  authUid = "",
} = {}) {
  const allForEmail = listInvitesForContactEmail(invites, emailLower);
  const uid = safeStr(authUid);
  const redeemed = allForEmail.filter((inv) => isInviteRedeemed(inv));
  const unused = allForEmail.filter((inv) => !isInviteRedeemed(inv));

  let primaryInvite = null;
  let status = CANONICAL_INVITE_STATUS.NONE;

  if (uid) {
    const matchUid = redeemed
      .filter((inv) => safeStr(inv.usedBy) === uid)
      .sort((a, b) => sortByNewest(a, b, "usedAt") || sortByNewest(a, b, "emailSentAt") || sortByNewest(a, b, "createdAt"));
    if (matchUid[0]) {
      primaryInvite = matchUid[0];
      status = CANONICAL_INVITE_STATUS.REDEEMED;
    }
  }

  if (!primaryInvite && redeemed.length) {
    primaryInvite = [...redeemed].sort(
      (a, b) =>
        sortByNewest(a, b, "usedAt") ||
        sortByNewest(a, b, "emailSentAt") ||
        sortByNewest(a, b, "createdAt")
    )[0];
    status = CANONICAL_INVITE_STATUS.REDEEMED;
  }

  if (!primaryInvite) {
    const sent = allForEmail
      .filter((inv) => isInviteEmailSent(inv))
      .sort((a, b) => sortByNewest(a, b, "emailSentAt") || sortByNewest(a, b, "createdAt"));
    if (sent[0]) {
      primaryInvite = sent[0];
      status = isInviteRedeemed(sent[0])
        ? CANONICAL_INVITE_STATUS.REDEEMED
        : CANONICAL_INVITE_STATUS.SENT_UNUSED;
    }
  }

  if (!primaryInvite && unused.length) {
    primaryInvite = unused[0];
    status = isInviteEmailSent(primaryInvite)
      ? CANONICAL_INVITE_STATUS.SENT_UNUSED
      : CANONICAL_INVITE_STATUS.PREPARED_UNUSED;
  }

  const staleUnusedInvites = unused.filter((inv) => inv.id !== primaryInvite?.id);
  const historyInvites = allForEmail.filter((inv) => inv.id !== primaryInvite?.id);

  let statusLabel = "No invitation";
  let detail = "No portal invite on file for this email.";
  if (status === CANONICAL_INVITE_STATUS.REDEEMED) {
    statusLabel = "Invite redeemed";
    detail = `Redeemed invite ${safeStr(primaryInvite.id)}${
      isInviteEmailSent(primaryInvite) ? " (email was sent via portal)" : ""
    }.`;
  } else if (status === CANONICAL_INVITE_STATUS.SENT_UNUSED) {
    statusLabel = "Invite sent — awaiting signup";
    detail = `Sent invite ${safeStr(primaryInvite.id)} — unused until the client completes signup.`;
  } else if (status === CANONICAL_INVITE_STATUS.PREPARED_UNUSED) {
    statusLabel = "Invitation prepared — email not sent.";
    detail = `Unused invite ${safeStr(primaryInvite.id)} — email not sent via portal.`;
  }

  if (staleUnusedInvites.length) {
    detail += ` ${staleUnusedInvites.length} stale unused invite(s) superseded and should not drive onboarding.`;
  }

  return {
    primaryInvite,
    status,
    statusLabel,
    detail,
    staleUnusedInvites,
    historyInvites,
    allForEmail,
    canInvalidateStale: staleUnusedInvites.length > 0,
  };
}

/**
 * Payload to mark a stale unused invite superseded (admin write).
 */
export function buildInvalidateStaleInvitePayload({ reason = "superseded_by_canonical_invite" } = {}) {
  return {
    invalidated: true,
    adminInvalidated: true,
    adminInvalidatedReason: safeStr(reason) || "superseded_by_canonical_invite",
  };
}

export const INVITED_EMAIL_HELP =
  "This invitation is assigned to this email. Contact Mark if it is incorrect.";
